/**
 * Welcome to WorldMapBubbleGiftDynamicLayer. This manages the visible side of the bubble gifts on World Map
 *
 * Other files you may want to look at
 * bubbleGiftsMgr.js - Only does the data side
 * BubbleGifts(IAP).js - The level and gifts
 * G.MapTutHand.js - Points at the bubble gift (or level node)
 * WorldMap.js - It really is important
 */
export default class WorldMapBubbleGiftDynamicLayer extends Phaser.Group {
  constructor(map, editorMode) {
    super(game);

    this.bubbleGiftsMgr = G.Helpers.bubbleGiftsMgr; // Alias
    this.levelDataMgr = G.Helpers.levelDataMgr; // Alias

    this.position = map.position;
    this.map = map; // Need reference to map
    this.editorMode = editorMode;

    /**
     *  This free Instance thing is similar to G.WorldMapChestDynamicLater's instance thing
     * It reuses objects and just re-inits them when it gets on screen
     */
    this.freeInstances = [];

    if (editorMode) {
      this._initEditor();
    } else {
      this._init();
    }

    G.sb('onWindowOpened').add(this.lockInput, this);
    G.sb('onWindowClosed').add(this.unlockInput, this);
  }

  /**
   * BubbleGiftDynamicLayer becomes inited!
   */
  _init() {
    this.positionsOfLevels = [];

    const maxLevelNumber = this.levelDataMgr.getMaxLevelIndex() + 1;

    this.activeBubbleGiftsData = this.bubbleGiftsMgr.getUnopenedBubblesLevels()
      .filter((levelNumber) => levelNumber <= maxLevelNumber)
      .map((levelNumber) => ({
        levelNumber,
        mapY: this.levelDataMgr.getMapPosition(levelNumber - 1).y,
      }));

    G.sb('onWindowOpened').add(this.lockInput, this);
    G.sb('onWindowClosed').add(this.unlockInput, this);

    this.onDestroy.add(() => {
      this.freeInstances.forEach((child) => {
        child.destroy();
      });
    }, this);

    this.map.signals.onMapScrolled.add(G.Helpers.callOnThreshold(100, this.checkInstancesPlacements, this));
  }

  /**
   * Bubble gifts are placed in editormode, but the positioning might be a little off because the map
   * isn't the perfect size
   */
  _initEditor() {
    this.bubbleGiftsMgr.getAllBubblesLevels().forEach((levelNumber) => {
      this.placeInstance({
        levelNumber,
      });
    }, this);
    this.update = () => {};
  }

  /**
   * Unlocks bubbles to be clicked
   */
  unlockInput() {
    this.ignoreChildInput = false;
    this.children.forEach((child) => { child.ignoreChildInput = false; }); // eslint-disable-line no-param-reassign
  }

  /**
   * Locks bubbles to be clicked
   */
  lockInput() {
    this.ignoreChildInput = true;
    this.children.forEach((child) => { child.ignoreChildInput = true; }); // eslint-disable-line no-param-reassign
  }

  /**
   * Checks instances of bubbles, and if they should show or hide
   * Kind of a graphic culling thing
   */
  checkInstancesPlacements() {
    for (let i = 0, len = this.activeBubbleGiftsData.length; i < len; i++) {
      const bubbleData = this.activeBubbleGiftsData[i];

      const wy = this.y + bubbleData.mapY;
      if ((wy > -200 && wy < game.height + 200)) {
        if (!bubbleData.instance && !G.Helpers.bubbleGiftsMgr.isBubbleOpened(bubbleData.levelNumber)) {
          this.placeInstance(bubbleData);
        }
      } else if (bubbleData.instance) {
        this.hideInstance(bubbleData);
      }
    }
  }

  /**
   * Gets a bubble slot and re-inits it
   * @param {Object} bubbleData
   */
  placeInstance(bubbleData) {
    bubbleData.instance = this.getFreeInstance(); // eslint-disable-line no-param-reassign
    bubbleData.instance.init(bubbleData.levelNumber, this.getBubblePositionByLevel(bubbleData.levelNumber));
    this.add(bubbleData.instance);
  }

  /**
   * Kills the bubble and frees it.
   * @param {Object} bubbleData
   */
  hideInstance(bubbleData) {
    bubbleData.instance.hide();
    bubbleData.instance.parent.removeChild(bubbleData.instance);
    this.freeInstances.push(bubbleData.instance);
    bubbleData.instance = null; // eslint-disable-line no-param-reassign
  }

  /**
   * Grabs a free instance of the bubble or make one if there isn't any
   */
  getFreeInstance() {
    return this.freeInstances.shift() || new BubbleGift(this.editorMode); // eslint-disable-line no-use-before-define
  }

  /**
   * Some weird trigonometry calculation that positions the bubble near the level.
   * Check comments inside for more detail
   * @param {number} levelNumber
   */
  getBubblePositionByLevel(levelNumber) {
    const bubblePos = new Phaser.Point();
    const lvlData = G.Helpers.levelDataMgr.getLevelByNumber(levelNumber); // Finds level node by number
    if (lvlData) {
      const nextLvlData = G.Helpers.levelDataMgr.getLevelByNumber(levelNumber + 2); // Finds level node + 2
      const prevLvlData = G.Helpers.levelDataMgr.getLevelByNumber(levelNumber - 2); // Finds level node - 2

      const vec2CurPrev = new Phaser.Point(prevLvlData.mapX - lvlData.mapX, prevLvlData.mapY - lvlData.mapY); // Calculates vector of current to previous node

      if (nextLvlData !== null) { // If theres a level node that is 2 levels away
        const vec2CurNext = new Phaser.Point(nextLvlData.mapX - lvlData.mapX, nextLvlData.mapY - lvlData.mapY); // Calculates vector of current to next node
        const angle = G.Utils.CalcHelper.angleBetweenPoints(vec2CurPrev, vec2CurNext); // Finds the angle between the 2 vectors
        if (angle < 145) { // If the angle is too small, add in the vector of the next node and hope for the best :'D
          bubblePos.x = prevLvlData.mapX + vec2CurNext.x;
          bubblePos.y = prevLvlData.mapY + vec2CurNext.y;
        } else { // Otherwise rotate it and put it there
          const rotatedPoint = vec2CurNext.clone();
          rotatedPoint.rotate(0, 0, 90, true);
          bubblePos.x = nextLvlData.mapX + rotatedPoint.y;
          bubblePos.y = nextLvlData.mapY + rotatedPoint.x;
        }
      } else { // If there is no level node 2 levels away (in the case of at the last levels)
        bubblePos.x += 190; // Splat that on
      }

      // Sanity checking
      const halfMapWidth = this.map.getTileMapWidth() / 2; // Get the half map width
      const assumedBubbleWidth = 42;
      if (Math.abs(bubblePos.x) + assumedBubbleWidth > halfMapWidth) { // Is the bubble really far from the half map width? (in any direction)
        const edgePadding = 10;
        if (bubblePos.x > 0) { // It is in the positive direction
          bubblePos.x = halfMapWidth - assumedBubbleWidth - edgePadding; // Go to the half map point (on the right) and push in until it hits the edge
        } else {
          bubblePos.x = -halfMapWidth + assumedBubbleWidth + edgePadding; // Go to the half map point (on the left) and push in until it hits the edge
        }
      }
    }

    return bubblePos;
  }
}

/**
 * The bubble object instance. Doesn't keep data very well.
 */
export class BubbleGift extends Phaser.Group {
  constructor(editorMode) {
    super(game);

    this.state = game.state.getCurrentState();

    this.editorMode = editorMode;

    // Image
    this.iconImg = G.makeImage(0, 0, null, 0.5, this);
    this.bubbleImg = G.makeImage(0, 0, null, 0.5, this);
    this.bubbleImg2 = G.makeImage(0, 0, null, 0.5, this);
    this.bubbleImg2.blendMode = 1;
    this.bubbleImg2.scale = this.bubbleImg.scale;
    this.bubbleImg2.alpha = 0.3;
    game.add.tween(this.bubbleImg2)
      .to({ alpha: 0 }, 500, Phaser.Easing.Sinusoidal.InOut, true, 0, -1, true);

    game.add.tween(this.pivot).to({
      y: 20,
    }, 2600, Phaser.Easing.Sinusoidal.InOut, true, 0, -1, true);
  }

  /**
   * Init gets called often when scrolling through the map.
   * Data in here changes a lot.
   * @param {number} levelNumber
   * @param {Phaser.Point} bubblePosition
   */
  init(levelNumber, bubblePosition) {
    this.levelNumber = levelNumber; // Noted level number
    this.gift = G.Helpers.bubbleGiftsMgr.getBubbleGift(this.levelNumber); // Gets gift data by level number

    this.position.set(bubblePosition.x, bubblePosition.y); // Position is calculated above in WorldMapBubbleGiftDynamicLayer

    // Visuals...
    const icon = G.gift.getIcon(this.gift);
    G.changeTexture(this.iconImg, icon === 'coin_1' ? 'coin_3' : icon);
    this.iconImg.scale.setTo(icon === 'coin_3' ? 0.63 : 0.7);
    this.bubbleImg.scale.setTo(0.7);

    this.unlocked = (this.levelNumber - 1) <= G.saveState.getLastPassedLevelNr();

    G.changeTexture(this.bubbleImg, this.unlocked ? 'giftBubble_active' : 'giftBubble');
    G.changeTexture(this.bubbleImg2, this.unlocked ? 'giftBubble_active' : 'giftBubble');

    if (this.unlocked) {
      this.bubbleImg.inputEnabled = true;
      this.bubbleImg.input.useHandCursor = true;
      this.bubbleImg.events.onInputDown.addOnce(this.open, this);
      this.bubbleImg2.visible = true;
      this.alpha = 1;
    } else {
      this.bubbleImg.inputEnabled = false;
      this.bubbleImg2.visible = false;
      this.alpha = 0.75;
    }

    if (this.editorMode) {
      this._initEditor();
    }
  }

  /**
   * Editor mode
   */
  _initEditor() {
    this.scale.setTo(0.5);
    this.bubbleImg.inputEnabled = false;
    G.stopTweens(this.pivot);
    this.y += 30;
  }

  /**
   * Hides when out of view
   */
  hide() {
    G.stopTweens(this);
    G.stopTweens(this.bubbleImg);
    G.stopTweens(this.iconImg);
    this.bubbleImg.events.onInputDown.removeAll();
    if (this.rewardLabel) {
      this.rewardLabel.destroy();
      this.rewardLabel = null;
    }
  }

  /**
   * When clicked
   */
  open() {
    G.Helpers.bubbleGiftsMgr.markBubbleAsOpen(this.levelNumber);

    if (G.Helpers.bubbleGiftsMgr.showBotOptinIconAfter === 0) G.saveState.data.showOptinIcon = true;
    if (G.Helpers.bubbleGiftsMgr.showBotOptinPopupAfter === 0) G.sb('pushWindow').dispatch(['botOptIn', () => {}, this]);
    G.Helpers.bubbleGiftsMgr.showBotOptinIconAfter--;
    G.Helpers.bubbleGiftsMgr.showBotOptinPopupAfter--;

    DDNA.transactionHelper.trackRewards(this.gift, [], {
      transactionType: 'REWARD', tActionType: 'REWARD', tGameArea: 'MAP',
    });

    OMT.transactionTracking.logInventoryTransactionBegin();
    G.gift.applyGift(this.gift, false, 'Reward', 'BubbleGift');
    OMT.transactionTracking.logInventoryTransactionEnd();

    G.sb('onBubbleGiftOpened').dispatch(this.levelNumber);

    G.sfx.pop.play();
    game.time.events.add(300, G.sfx.line.play, G.sfx.line);

    this.bubbleImg.inputEnabled = false;

    const str = G.gift.getLabelString(this.gift, 1.2);

    game.add.tween(this.bubbleImg.scale)
      .to({ x: 0, y: 0 }, 300, Phaser.Easing.Cubic.In, true);
    game.add.tween(this.iconImg.scale)
      .to({ x: 0, y: 0 }, 300, Phaser.Easing.Cubic.In, true);

    this.rewardLabel = this.add(new G.LabelTextT(
      str, 0, 0, {
        style: 'font-white',
        fontSize: '50px',
      }, 0.5, 300,
    ));
    this.rewardLabel.scale.setTo(0);
    game.add.tween(this.rewardLabel.scale)
      .to({ x: 1, y: 1 }, 400, Phaser.Easing.Elastic.Out, true, 300);

    game.add.tween(this)
      .to({ alpha: 0 }, 400, Phaser.Easing.Sinusoidal.Out, true, 1200);
  }
}
